import { api } from '../lib/api';
import type {
  LoginRequest,
  LoginResponse,
  RegisterCompanyRequest,
  RegisterCandidateRequest,
  User,
  Company,
  Candidate,
} from '../types';

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>(
      '/auth/login',
      credentials
    );

    if (response.token) {
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('user_type', response.user.type);
    }

    return response;
  }

  async registerCompany(data: RegisterCompanyRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>(
      '/auth/register/company',
      data
    );

    if (response.token) {
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('user_type', 'company');
    }

    return response;
  }

  async registerCandidate(data: RegisterCandidateRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>(
      '/auth/register/candidate',
      data
    );

    if (response.token) {
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('user_type', 'candidate');
    }

    return response;
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignora erro no logout remoto
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_session');
      localStorage.removeItem('user');
      localStorage.removeItem('user_type');
      // Limpa cookie frontend_user
      document.cookie = 'frontend_user=; path=/; domain=.h2k.com.br; max-age=0';
    }
  }

  /**
   * Detecta sessão ativa do backend lendo o cookie `frontend_user`.
   *
   * O backend seta este cookie (não-httpOnly, domain=.h2k.com.br) após login:
   *   frontend_user={"id":1,"name":"João","type":"candidate","token":"xxx"}
   *
   * Nenhuma chamada API é feita — apenas leitura de cookie.
   * Isso evita interferir com a sessão do Laravel/Livewire.
   */
  checkSession(): (User | Company | Candidate) | null {
    // Se já tem token ou sessão detectada, retorna dados salvos
    if (this.getToken() || localStorage.getItem('auth_session')) return this.getUser();

    try {
      // Debug: log todos os cookies disponíveis
      console.log('[Auth] Todos os cookies:', document.cookie);
      
      // Lê cookie frontend_user setado pelo backend após login
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('frontend_user='))
        ?.split('=')
        .slice(1)
        .join('=');

      console.log('[Auth] Cookie frontend_user:', cookieValue);

      if (!cookieValue) return null;

      const user = JSON.parse(decodeURIComponent(cookieValue));
      if (!user || !user.id) return null;

      // Salva token se existir no cookie
      if (user.token) {
        localStorage.setItem('auth_token', user.token);
        console.log('[Auth] Token salvo do cookie:', user.token);
      }
      
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('user_type', user.type || 'candidate');
      localStorage.setItem('auth_session', 'true');
      return user;
    } catch (e) {
      console.error('[Auth] Erro ao ler cookie:', e);
      return null;
    }
  }

  async me(): Promise<User | Company | Candidate> {
    const response = await api.get<any>('/auth/me');
    // Backend pode retornar { user, profile } ou diretamente o user
    const user = response.user || response;
    const fullUser = response.profile || user;
    localStorage.setItem('user', JSON.stringify(fullUser));
    if (user.type) localStorage.setItem('user_type', user.type);
    return fullUser;
  }

  async resendVerificationEmail(): Promise<{ success: boolean; message?: string }> {
    try {
      const resp = await api.post<{ success: boolean; message?: string }>(
        '/auth/email/resend',
        {}
      );
      return resp;
    } catch (err) {
      // Fallback em rotas comuns do Laravel
      const resp = await api.post<{ success: boolean; message?: string }>(
        '/email/verification-notification',
        {}
      );
      return resp;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await api.post<void>('/auth/forgot-password', { email });
  }

  async resetPassword(
    token: string,
    password: string,
    password_confirmation: string,
    email?: string,
  ): Promise<void> {
    const payload: Record<string, string> = {
      token,
      password,
      password_confirmation,
    };
    if (email) payload.email = email;
    await api.post<void>('/auth/reset-password', payload);
  }

  async verifyEmail(params: Record<string, string>): Promise<{ success: boolean; message?: string }> {
    // Tenta via GET com query params (mais comum para verificação por link)
    try {
      const resp = await api.get<{ success: boolean; message?: string }>(
        '/auth/email/verify',
        { params }
      );
      return resp;
    } catch (err: any) {
      // Como fallback, tenta POST com body (caso o backend exija)
      const resp = await api.post<{ success: boolean; message?: string }>(
        '/auth/email/verify',
        params
      );
      return resp;
    }
  }

  getToken(): string | null {
    const token = localStorage.getItem('auth_token');
    if (token) return token;

    // Fallback 1: extrai token do user salvo no localStorage
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.token) {
          localStorage.setItem('auth_token', user.token);
          return user.token;
        }
      }
    } catch {}

    // Fallback 2: tenta ler do cookie frontend_user (setado pelo painel Livewire)
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('frontend_user='))
        ?.split('=')
        .slice(1)
        .join('=');
      if (cookieValue) {
        const user = JSON.parse(decodeURIComponent(cookieValue));
        if (user?.token) {
          localStorage.setItem('auth_token', user.token);
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('user_type', user.type || 'candidate');
          return user.token;
        }
      }
    } catch {}

    return null;
  }

  getUser(): User | Company | Candidate | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getUserType(): 'company' | 'candidate' | 'admin' | null {
    return localStorage.getItem('user_type') as 'company' | 'candidate' | 'admin' | null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isCompany(): boolean {
    return this.getUserType() === 'company';
  }

  isCandidate(): boolean {
    return this.getUserType() === 'candidate';
  }

  isAdmin(): boolean {
    return this.getUserType() === 'admin';
  }
}

export const authService = new AuthService();
